import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "prompt": "A cinematic view of a bustling city street at night with neon lights and rain",
 *   "durationSeconds": 8,
 *   "aspectRatio": "16:9",
 *   "resolution": "1080p",
 *   "compressionQuality": "optimized",
 *   "enhancePrompt": true,
 *   "generateAudio": true,
 *   "personGeneration": "allow_adult",
 *   "sampleCount": 1
 * }
 * ```
 */
export interface GoogleVeo30Generate001Params {
  /**
   * 视频生成提示词，描述要生成的视频内容，长度不超过2000字符
   */
  prompt: string;
  /**
   * 输入图像，用于图像到视频(I2V)模式，支持URL、base64数据（data:image/...格式）或包含bytesBase64Encoded/gcsUri的对象
   */
  image?: string | any;
  /**
   * 负向提示词，描述不希望在视频中出现的内容
   */
  negativePrompt?: string;
  /**
   * 视频时长（秒），支持4、6或8秒，默认8秒
   * @default 8
   * @values 4 | 6 | 8
   */
  durationSeconds?: 4 | 6 | 8;
  /**
   * 视频宽高比，支持9:16（竖屏）或16:9（横屏），默认16:9
   * @default "16:9"
   * @values 9:16 | 16:9
   */
  aspectRatio?: '9:16' | '16:9';
  /**
   * 视频分辨率，支持720p或1080p，默认720p。Veo 3.0新增功能，用于控制输出视频的清晰度
   * @default "720p"
   * @values 720p | 1080p
   */
  resolution?: '720p' | '1080p';
  /**
   * 压缩质量，可选值：optimized（优化，较小文件）或 lossless（无损，较大文件），默认optimized
   * @default "optimized"
   * @values optimized | lossless
   */
  compressionQuality?: 'optimized' | 'lossless';
  /**
   * 是否启用提示词增强，开启后会自动优化和扩展提示词，默认true
   * @default true
   */
  enhancePrompt?: boolean;
  /**
   * 是否生成音频，Veo 3.0新增功能，可为视频生成配套音频，默认true
   * @default true
   */
  generateAudio?: boolean;
  /**
   * 人物生成设置，可选值：dont_allow（不允许生成人物）或 allow_adult（允许生成成年人），默认allow_adult
   * @default "allow_adult"
   * @values dont_allow | allow_adult
   */
  personGeneration?: 'dont_allow' | 'allow_adult';
  /**
   * 生成样本数量，控制一次生成多少个视频，范围1-4，默认1
   * @default 1
   * @range 1 - 4
   */
  sampleCount?: number;
  /**
   * 随机种子，用于生成可重复的结果，范围0-4294967295
   * @range 0 - 4294967295
   */
  seed?: number;
  /**
   * GCS存储URI，用于指定视频存储位置，格式如 gs://bucket-name/path/。如不指定则返回base64编码的视频
   */
  storageUri?: string;
}

export interface GoogleVeo30Generate001Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * google_veo_30_generate_001
 *
 * Google Veo 3.0 Generate 001 视频生成接口，支持文本到视频(T2V)和图像到视频(I2V)生成，提供分辨率和压缩选项。
 * 
 * Model: veo-3.0-generate-001
 * Features:
 * - 文本到视频生成（T2V）
 * - 图像到视频生成（I2V）
 * - 支持720p和1080p分辨率
 * - 可选压缩质量（优化或无损）
 * - 音频生成功能（Veo 3.0新增）
 * - 提示词增强功能
 *
 * @example
 * ```typescript
 * import { initSeacloud, googleVeo30Generate001 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await googleVeo30Generate001(
 *   {
 *     "prompt": "A cinematic view of a bustling city street at night with neon lights and rain",
 *     "durationSeconds": 8,
 *     "aspectRatio": "16:9",
 *     "resolution": "1080p",
 *     "compressionQuality": "optimized",
 *     "enhancePrompt": true,
 *     "generateAudio": true,
 *     "personGeneration": "allow_adult",
 *     "sampleCount": 1
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 GoogleVeo30Generate001Params 接口定义
 * @returns 生成的资源列表
 */
export async function googleVeo30Generate001(
  params: GoogleVeo30Generate001Params,
): Promise<GoogleVeo30Generate001Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'google_veo_30_generate_001',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: GoogleVeo30Generate001Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
